(() => {
  type FileState = "available" | "review_pending" | "archiving" | "archived";
  type ShareState = "none" | "active" | "revoked" | "expired";
  type DrawerTab = "all" | "artifact" | "document" | "media" | "file" | "link";
  type FileCategory = "document" | "media" | "file";
  type ArtifactDisplay = "image-set" | "video" | "pdf" | "file" | "link" | "markdown";
  type ExternalArtifactProvider = "claude" | "chatgpt" | "chatgpt_sites";
  type PublicFile = {
    id: string;
    displayName: string;
    mediaType: string;
    size: number;
    createdAt: string;
    state: FileState;
    contentUrl: string;
    shareState: ShareState;
  };
  type PublicLink = {
    id: string;
    display: "link";
    title: string;
    payload: { url: string };
    createdAt: string;
    sessionId: string;
    roomId: string;
    context: string | null;
    sourceLabel: string | null;
    resourceType: "link";
    custody: "link_reference";
    access: "unknown";
    availability: "unknown";
    firstSeenAt: string;
    occurrenceCount: number;
  };
  type PublicExternalArtifact = Omit<PublicLink, "resourceType" | "custody"> & {
    resourceType: "external_artifact";
    provider: ExternalArtifactProvider;
    custody: "external_reference";
  };
  type PublicArtifact = {
    id: string;
    kind: string;
    display: ArtifactDisplay;
    title: string;
    payload: Record<string, unknown>;
    createdAt: string;
    sessionId: string;
    roomId: string;
    materializedFileIds: string[];
  };
  type LinkPreviewPayload = {
    url: string;
    title: string;
    description?: string;
    excerpt?: string;
    image?: string | null;
    siteName?: string;
  };
  type LinkPreviewJob = {
    anchors: Set<HTMLAnchorElement>;
    link: PreviewableLink;
    generation: number;
    searchRequested: boolean;
  };
  type PreviewableLink = PublicLink | PublicExternalArtifact;
  type DrawerItem = { kind: "file"; createdAt: string; category: FileCategory; file: PublicFile }
    | { kind: "artifact"; createdAt: string; artifact: PublicArtifact }
    | { kind: "external_artifact"; createdAt: string; artifact: PublicExternalArtifact }
    | { kind: "link"; createdAt: string; link: PublicLink };
  type DrawerApi = {
    open(): Promise<void>;
    close(): void;
    setRoom(roomId: string | null): void;
    reconcileRoomMembership(): void;
    getState(): { roomId: string | null; view: string; fileIds: string[] };
  };

  const FILE_ID_RE = /^file:[A-Za-z0-9._-]+$/;
  const MESSAGE_LINK_ID_RE = /^message-link:(\d+):\d+$/;
  const SHARE_URL_RE = /^\/shared\/files\/[A-Za-z0-9_-]{43}$/;
  const FILE_STATES = new Set<FileState>(["available", "review_pending", "archiving", "archived"]);
  const SHARE_STATES = new Set<ShareState>(["none", "active", "revoked", "expired"]);
  const ARTIFACT_DISPLAYS = new Set<ArtifactDisplay>(["image-set", "video", "pdf", "file", "link", "markdown"]);
  const EXTERNAL_ARTIFACT_PROVIDERS = new Set<ExternalArtifactProvider>(["claude", "chatgpt", "chatgpt_sites"]);
  const DOCUMENT_EXTENSIONS = new Set(["pdf", "txt", "md", "doc", "docx", "hwp", "hwpx", "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp", "rtf", "csv"]);
  const MEDIA_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "tif", "tiff", "mp4", "m4v", "mov", "webm", "ogv", "mp3", "m4a", "wav", "ogg", "oga", "flac", "aac"]);
  const LINK_PREVIEW_CONCURRENCY = 3;

  let activeRoomId: string | null = null;
  let activeSessionId: string | null = null;
  let files: PublicFile[] = [];
  let artifacts: PublicArtifact[] = [];
  let externalArtifacts: PublicExternalArtifact[] = [];
  let links: PublicLink[] = [];
  let activeTab: DrawerTab = "all";
  let searchQuery = "";
  let viewState = "idle";
  let generation = 0;
  let actionFileId: string | null = null;
  let listRequestId = 0;
  let initialized = false;
  let linkPreviewActive = 0;
  let linkPreviewQueue: LinkPreviewJob[] = [];
  let linkPreviewObserver: IntersectionObserver | null = null;
  const linkPreviewTargets = new WeakMap<Element, { link: PreviewableLink; generation: number }>();
  const linkPreviewCache = new Map<string, Promise<LinkPreviewPayload | null>>();
  const linkPreviewValues = new Map<string, LinkPreviewPayload>();
  const linkPreviewResolvedUrls = new Set<string>();
  const linkPreviewPending = new Map<string, LinkPreviewJob>();

  const root = () => document.getElementById("roomFileDrawer");
  const toggle = () => document.getElementById("roomFileDrawerToggle") as HTMLButtonElement | null;
  const list = () => document.getElementById("roomFileDrawerList");
  const search = () => document.getElementById("roomFileDrawerSearch") as HTMLInputElement | null;
  const resizer = () => document.getElementById("roomFileDrawerResizer");
  const chatView = () => document.getElementById("view-chat");

  // ===== 서랍 폭 조절 =====
  // 드래그·클램프·저장·키보드는 utils/panel-resizer가 소유한다(미리보기 패널과 공유).
  // 여기서는 이 표면의 좌표(변수 이름·범위·가용 폭 측정 기준)만 정한다.
  let widthResizer: PanelResizer | null = null;

  function initResizer(): void {
    const handle = resizer();
    if (!handle || typeof attachPanelResizer !== "function") return;
    widthResizer = attachPanelResizer({
      handle,
      target: chatView(),
      cssVariable: "--room-file-drawer-width",
      storageKey: "life-os-room-file-drawer-width",
      defaultWidth: 360,
      minWidth: 280,
      maxWidth: 720,
      // 서랍이 대화를 통째로 덮지 않도록 남겨 두는 최소 대화 폭.
      minRemaining: 200,
      measureAvailable: () => chatView()?.clientWidth || 0,
    });
  }

  function setResizerVisible(visible: boolean): void {
    widthResizer?.setVisible(visible);
  }

  function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function isPublicFile(value: unknown): value is PublicFile {
    if (!isRecord(value)
      || typeof value.id !== "string" || !FILE_ID_RE.test(value.id)
      || typeof value.displayName !== "string" || !value.displayName.trim()
      || typeof value.mediaType !== "string"
      || typeof value.size !== "number" || !Number.isSafeInteger(value.size) || value.size < 0
      || typeof value.createdAt !== "string" || !Number.isFinite(Date.parse(value.createdAt))
      || typeof value.state !== "string" || !FILE_STATES.has(value.state as FileState)
      || typeof value.contentUrl !== "string"
      || typeof value.shareState !== "string" || !SHARE_STATES.has(value.shareState as ShareState)) return false;
    return value.contentUrl === `/api/files/${encodeURIComponent(value.id)}/content`;
  }

  function normalizeSafeWebUrl(value: unknown): string | null {
    if (typeof value !== "string") return null;
    try {
      const parsed = new URL(value);
      return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
    } catch (_error: unknown) {
      return null;
    }
  }

  function projectPublicArtifact(value: unknown, roomId: string, sessionId: string): PublicArtifact | null {
    if (!isRecord(value) || typeof value.id !== "string" || !value.id
      || typeof value.kind !== "string" || !value.kind.trim()
      || typeof value.display !== "string" || !ARTIFACT_DISPLAYS.has(value.display as ArtifactDisplay)
      || typeof value.title !== "string" || !value.title.trim()
      || typeof value.createdAt !== "string" || !Number.isFinite(Date.parse(value.createdAt))
      || value.roomId !== roomId || value.sessionId !== sessionId || !isRecord(value.payload)
      || !Array.isArray(value.materializedFileIds)
      || !value.materializedFileIds.every((id) => typeof id === "string" && FILE_ID_RE.test(id))) return null;
    const safeLinkUrl = value.display === "link" ? normalizeSafeWebUrl(value.payload.url) : null;
    if (value.display === "link" && !safeLinkUrl) return null;
    return {
      id: value.id,
      kind: value.kind.trim(),
      display: value.display as ArtifactDisplay,
      title: value.title.trim(),
      payload: safeLinkUrl ? { ...value.payload, url: safeLinkUrl } : value.payload,
      createdAt: value.createdAt,
      sessionId,
      roomId,
      materializedFileIds: [...value.materializedFileIds] as string[],
    };
  }

  function projectMessageLink(value: unknown, roomId: string, sessionId: string): PublicLink | PublicExternalArtifact | null {
    if (!isRecord(value)) return null;
    const idMatch = typeof value.id === "string" ? value.id.match(MESSAGE_LINK_ID_RE) : null;
    if (!idMatch || typeof value.seq !== "number" || !Number.isSafeInteger(value.seq) || value.seq < 0
      || Number(idMatch[1]) !== value.seq
      || (value.resourceType !== "link" && value.resourceType !== "external_artifact")
      || typeof value.title !== "string" || !value.title.trim()
      || typeof value.createdAt !== "string" || !Number.isFinite(Date.parse(value.createdAt))
      || !(value.context === null || typeof value.context === "string")
      || !(value.sourceLabel === null || typeof value.sourceLabel === "string")
      || value.access !== "unknown" || value.availability !== "unknown"
      || typeof value.firstSeenAt !== "string" || !Number.isFinite(Date.parse(value.firstSeenAt))
      || typeof value.occurrenceCount !== "number" || !Number.isSafeInteger(value.occurrenceCount) || value.occurrenceCount < 1) return null;
    const url = normalizeSafeWebUrl(value.url);
    if (!url) return null;
    const base = {
      id: value.id as string,
      display: "link",
      title: value.title.trim(),
      payload: { url },
      createdAt: value.createdAt,
      sessionId,
      roomId,
      context: typeof value.context === "string" && value.context.trim() ? value.context.trim().slice(0, 500) : null,
      sourceLabel: typeof value.sourceLabel === "string" && value.sourceLabel.trim() ? value.sourceLabel.trim().slice(0, 120) : null,
      access: "unknown" as const,
      availability: "unknown" as const,
      firstSeenAt: value.firstSeenAt,
      occurrenceCount: value.occurrenceCount,
    };
    if (value.resourceType === "external_artifact") {
      if (value.custody !== "external_reference" || typeof value.provider !== "string"
        || !EXTERNAL_ARTIFACT_PROVIDERS.has(value.provider as ExternalArtifactProvider)) return null;
      return { ...base, display: "link", resourceType: "external_artifact", provider: value.provider as ExternalArtifactProvider, custody: "external_reference" };
    }
    if (value.provider !== null || value.custody !== "link_reference") return null;
    return { ...base, display: "link", resourceType: "link", custody: "link_reference" };
  }

  function projectLinkPreview(value: unknown): LinkPreviewPayload | null {
    if (!isRecord(value) || typeof value.title !== "string" || !value.title.trim()) return null;
    const url = normalizeSafeWebUrl(value.url);
    if (!url) return null;
    const image = value.image === null || value.image === undefined ? null : normalizeSafeWebUrl(value.image);
    return {
      url,
      title: value.title.trim(),
      ...(typeof value.description === "string" && value.description.trim() ? { description: value.description.trim() } : {}),
      ...(typeof value.excerpt === "string" && value.excerpt.trim() ? { excerpt: value.excerpt.trim() } : {}),
      ...(image ? { image } : {}),
      ...(typeof value.siteName === "string" && value.siteName.trim() ? { siteName: value.siteName.trim() } : {}),
    };
  }

  function setStatus(text: string, kind = "neutral"): void {
    const element = document.getElementById("roomFileDrawerStatus");
    if (!element) return;
    element.textContent = text;
    element.dataset.kind = kind;
  }

  function replaceList(message: string): void {
    const container = list();
    if (!container) return;
    const empty = document.createElement("p");
    empty.className = "room-file-drawer-message";
    empty.textContent = message;
    container.replaceChildren(empty);
  }

  function formatSize(size: number): string {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`;
    return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  }

  function formatDate(value: string): string {
    return new Date(value).toLocaleDateString("ko-KR", { year: "numeric", month: "short", day: "numeric" });
  }

  function stateLabel(state: FileState): string {
    if (state === "available") return "보관 중";
    if (state === "archived") return "장기 보관";
    if (state === "archiving") return "보관 이동 중";
    return "검토 필요";
  }

  function mediaLabel(mediaType: string): string {
    if (mediaType === "application/pdf") return "PDF";
    if (mediaType.startsWith("image/")) return "이미지";
    if (mediaType.startsWith("video/")) return "동영상";
    if (mediaType.startsWith("text/")) return "문서";
    return "파일";
  }

  function fileExtension(file: PublicFile): string {
    const match = /\.([A-Za-z0-9]+)$/.exec(file.displayName);
    return match ? match[1].toLowerCase() : "";
  }

  function fileCategory(file: PublicFile): FileCategory {
    const mediaType = file.mediaType.split(";", 1)[0].trim().toLowerCase();
    const extension = fileExtension(file);
    if (mediaType === "application/pdf" || mediaType.startsWith("text/") || DOCUMENT_EXTENSIONS.has(extension)) return "document";
    if (mediaType.startsWith("image/") || mediaType.startsWith("video/") || mediaType.startsWith("audio/") || MEDIA_EXTENSIONS.has(extension)) return "media";
    return "file";
  }

  // 업로드 시 mimetype 이 비면 application/octet-stream 으로 굳으므로(routes/files.ts) 확장자
  // 폴백을 함께 본다 — 판정 규칙 자체는 ArtifactPanel 쪽 _fileKind 와 같은 계약이다.
  function isImageFile(file: PublicFile): boolean {
    return window.MediaViewerLauncher?.fileKind(file) === "image";
  }

  // content 는 available/archived 에서만 읽힌다(routes/files.ts) — 그 밖의 상태에서는
  // 썸네일도 미리보기도 열지 않는다.
  function isReadable(file: PublicFile): boolean {
    return file.state === "available" || file.state === "archived";
  }

  // 서버는 createdAt 오름차순으로 내려준다(lib/files/file-record-store.ts#sortRecords) —
  // 그 정렬은 lifecycle/archive 쪽도 공유하므로 건드리지 않고 서랍에서만 최신순으로 뒤집는다.
  function newestFirst(left: PublicFile, right: PublicFile): number {
    return right.createdAt.localeCompare(left.createdAt) || right.id.localeCompare(left.id);
  }

  function normalizedSearch(value: string): string {
    return value.normalize("NFKC").toLocaleLowerCase("ko-KR").trim();
  }

  function externalArtifactProviderLabel(provider: ExternalArtifactProvider): string {
    if (provider === "claude") return "Claude";
    return provider === "chatgpt" ? "ChatGPT" : "ChatGPT Sites";
  }

  function visibleItems(): DrawerItem[] {
    const needle = normalizedSearch(searchQuery);
    const fileItems: DrawerItem[] = files
      .map((file) => ({ kind: "file" as const, createdAt: file.createdAt, category: fileCategory(file), file }))
      .filter((item) => activeTab === "all" || item.category === activeTab)
      .filter((item) => !needle || normalizedSearch(item.file.displayName).includes(needle))
      .map((item) => item);
    const artifactItems: DrawerItem[] = (activeTab === "all" || activeTab === "artifact" ? artifacts : [])
      .filter((artifact) => !needle || normalizedSearch([
        artifact.title,
        artifact.kind,
        artifact.display,
        "BigtionAX",
      ].join(" ")).includes(needle))
      .map((artifact) => ({ kind: "artifact", createdAt: artifact.createdAt, artifact }));
    const externalArtifactItems: DrawerItem[] = (activeTab === "all" || activeTab === "artifact" ? externalArtifacts : [])
      .filter((artifact) => {
        const preview = linkPreviewValues.get(artifact.payload.url);
        return !needle || normalizedSearch([
          artifact.title,
          artifact.payload.url,
          artifact.context || "",
          artifact.sourceLabel || "",
          externalArtifactProviderLabel(artifact.provider),
          preview?.title || "",
          preview?.description || "",
          preview?.excerpt || "",
          preview?.siteName || "",
        ].join(" ")).includes(needle);
      })
      .map((artifact) => ({ kind: "external_artifact", createdAt: artifact.createdAt, artifact }));
    const linkItems: DrawerItem[] = (activeTab === "all" || activeTab === "link" ? links : [])
      .filter((link) => {
        if (!needle) return true;
        const preview = linkPreviewValues.get(link.payload.url);
        return normalizedSearch([
          link.title,
          link.payload.url,
          link.context || "",
          link.sourceLabel || "",
          preview?.title || "",
          preview?.description || "",
          preview?.excerpt || "",
          preview?.siteName || "",
        ].join(" ")).includes(needle);
      })
      .map((link) => ({ kind: "link", createdAt: link.createdAt, link }));
    return [...fileItems, ...artifactItems, ...externalArtifactItems, ...linkItems]
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  }

  function syncTabs(): void {
    document.querySelectorAll<HTMLButtonElement>("#roomFileDrawerTabs [data-file-tab]").forEach((button) => {
      button.setAttribute("aria-selected", String(button.dataset.fileTab === activeTab));
    });
  }

  function openPreview(file: PublicFile, trigger: HTMLElement | null): void {
    const mediaSiblings = isImageFile(file)
      ? files.filter((candidate) => isReadable(candidate) && isImageFile(candidate))
      : undefined;
    if (window.MediaViewerLauncher?.openFile(file, mediaSiblings)) return;
    const panel = window.ArtifactPanel;
    if (!panel || typeof panel.openFile !== "function") {
      window.open(file.contentUrl, "_blank", "noopener,noreferrer");
      return;
    }
    panel.openFile(file, { triggerEl: trigger });
  }

  function updateFile(fileId: string, patch: Partial<PublicFile>): void {
    files = files.map((file) => file.id === fileId ? { ...file, ...patch } : file);
  }

  function isCurrentOperation(roomId: string, operationGeneration: number): boolean {
    return activeRoomId === roomId && generation === operationGeneration;
  }

  async function copyOneTimeUrl(url: string): Promise<boolean> {
    if (!SHARE_URL_RE.test(url)) return false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      }
      const temporary = document.createElement("textarea");
      temporary.value = url;
      temporary.setAttribute("aria-hidden", "true");
      temporary.style.position = "fixed";
      temporary.style.opacity = "0";
      document.body.appendChild(temporary);
      temporary.select();
      const copied = document.execCommand("copy");
      temporary.value = "";
      temporary.remove();
      return copied;
    } catch (_error: unknown) {
      return false;
    }
  }

  async function mutateShare(file: PublicFile, operation: "issue" | "revoke" | "reissue"): Promise<void> {
    if (!activeRoomId || file.state !== "available" || actionFileId) return;
    const roomId = activeRoomId;
    const operationGeneration = generation;
    actionFileId = file.id;
    renderSuccess();
    setStatus(operation === "revoke" ? "공유 링크를 철회하는 중…" : "공유 링크를 준비하는 중…");
    try {
      const suffix = operation === "issue" ? "" : `/${operation}`;
      const response = await fetch(`/api/files/${encodeURIComponent(file.id)}/shares${suffix}`, { method: "POST" });
      const payload: unknown = await response.json().catch(() => ({}));
      if (!isCurrentOperation(roomId, operationGeneration)) return;
      if (!response.ok || !isRecord(payload) || payload.ok !== true) throw new Error("share_operation_failed");

      if (operation === "revoke") {
        updateFile(file.id, { shareState: "revoked" });
        setStatus("공유 링크를 즉시 철회했어요.", "success");
      } else {
        const share = isRecord(payload.share) ? payload.share : null;
        const url = share && typeof share.url === "string" ? share.url : "";
        const copied = await copyOneTimeUrl(url);
        if (!isCurrentOperation(roomId, operationGeneration)) return;
        updateFile(file.id, { shareState: "active" });
        setStatus(copied ? "새 공유 링크를 복사했어요." : "링크를 복사하지 못했어요. 다시 시도해 주세요.", copied ? "success" : "error");
      }
    } catch (_error: unknown) {
      if (isCurrentOperation(roomId, operationGeneration)) setStatus("공유 작업을 완료하지 못했어요.", "error");
    } finally {
      if (isCurrentOperation(roomId, operationGeneration)) {
        actionFileId = null;
        renderSuccess(true);
      }
    }
  }

  function actionButton(label: string, className: string, disabled: boolean, onClick: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.textContent = label;
    button.disabled = disabled;
    button.addEventListener("click", onClick);
    return button;
  }

  function renderFile(file: PublicFile): HTMLElement {
    const item = document.createElement("article");
    item.className = "room-file-item";
    item.dataset.fileId = file.id;

    const readable = isReadable(file);
    if (readable && isImageFile(file)) {
      const thumb = document.createElement("button");
      thumb.type = "button";
      thumb.className = "room-file-thumb";
      thumb.setAttribute("aria-label", `${file.displayName} 미리보기`);
      const preview = document.createElement("img");
      preview.src = file.contentUrl;
      preview.alt = "";
      preview.loading = "lazy";
      thumb.appendChild(preview);
      thumb.addEventListener("click", () => { openPreview(file, thumb); });
      item.append(thumb);
    }

    const top = document.createElement("div");
    top.className = "room-file-item-top";
    const name = actionButton(file.displayName, "room-file-name", !readable, () => {
      openPreview(file, name);
    });
    const badge = document.createElement("span");
    badge.className = `room-file-state room-file-state--${file.state}`;
    badge.textContent = stateLabel(file.state);
    top.append(name);
    if (file.shareState === "active") {
      const shareChip = document.createElement("span");
      shareChip.className = "room-file-share-chip";
      shareChip.textContent = "공유 중";
      top.append(shareChip);
    }
    top.append(badge);

    const meta = document.createElement("p");
    meta.className = "room-file-meta";
    meta.textContent = `${mediaLabel(file.mediaType)} · ${formatSize(file.size)} · ${formatDate(file.createdAt)}`;

    const actions = document.createElement("div");
    actions.className = "room-file-actions";
    const shareDisabled = file.state !== "available" || actionFileId !== null;
    if (file.shareState === "active") {
      actions.append(
        actionButton("철회", "room-file-action room-file-action--quiet", shareDisabled, () => { void mutateShare(file, "revoke"); }),
        actionButton("재발급", "room-file-action", shareDisabled, () => { void mutateShare(file, "reissue"); }),
      );
    } else {
      actions.append(actionButton("공유 링크 발급", "room-file-action", shareDisabled, () => { void mutateShare(file, "issue"); }));
    }

    item.append(top, meta, actions);
    return item;
  }

  function artifactDisplayLabel(display: ArtifactDisplay): string {
    if (display === "image-set") return "이미지 묶음";
    if (display === "video") return "동영상";
    if (display === "pdf") return "PDF";
    if (display === "markdown") return "문서";
    if (display === "link") return "링크형";
    return "파일형";
  }

  function openArtifact(artifact: PublicArtifact, trigger: HTMLElement): void {
    if (window.MediaViewerLauncher?.openArtifact(artifact)) return;
    if (window.ArtifactPanel?.open) {
      window.ArtifactPanel.open(artifact, trigger);
      return;
    }
    const fallback = normalizeSafeWebUrl(artifact.payload.url) || normalizeSafeWebUrl(artifact.payload.path);
    if (fallback) window.open(fallback, "_blank", "noopener,noreferrer");
  }

  function renderArtifact(artifact: PublicArtifact): HTMLElement {
    const item = document.createElement("article");
    item.className = "room-file-item room-file-artifact-item";
    item.dataset.artifactId = artifact.id;

    const top = document.createElement("div");
    top.className = "room-file-item-top";
    const name = actionButton(artifact.title, "room-file-name", false, () => openArtifact(artifact, name));
    const badge = document.createElement("span");
    badge.className = "room-file-origin-chip";
    badge.textContent = "BigtionAX";
    top.append(name, badge);

    const meta = document.createElement("p");
    meta.className = "room-file-meta";
    meta.textContent = `아티팩트 · ${artifactDisplayLabel(artifact.display)} · ${formatDate(artifact.createdAt)}`;
    item.append(top, meta);
    return item;
  }

  function renderExternalArtifact(artifact: PublicExternalArtifact): HTMLElement {
    const item = document.createElement("article");
    item.className = "room-file-item room-file-external-artifact-item";
    item.dataset.externalArtifactId = artifact.id;

    const anchor = document.createElement("a");
    anchor.className = "room-file-link-preview room-file-external-artifact-link";
    anchor.href = artifact.payload.url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.setAttribute("aria-label", `${artifact.title} 외부 서비스에서 열기`);

    const thumb = document.createElement("span");
    thumb.className = "room-file-link-thumb room-file-link-thumb--placeholder";
    thumb.textContent = "↗";
    thumb.setAttribute("aria-hidden", "true");
    const copy = document.createElement("span");
    copy.className = "room-file-link-copy";
    const site = document.createElement("span");
    site.className = "room-file-link-site";
    site.textContent = new URL(artifact.payload.url).hostname;
    const top = document.createElement("span");
    top.className = "room-file-external-artifact-top";
    const title = document.createElement("strong");
    title.className = "room-file-link-title room-file-external-artifact-title";
    title.textContent = artifact.title;
    const badge = document.createElement("span");
    badge.className = "room-file-origin-chip room-file-origin-chip--external";
    badge.textContent = externalArtifactProviderLabel(artifact.provider);
    top.append(title, badge);

    const description = document.createElement("span");
    description.className = "room-file-link-description";
    description.textContent = artifact.payload.url;
    const access = document.createElement("span");
    access.className = "room-file-external-artifact-access";
    access.textContent = "외부 서비스에서 접근 확인";
    copy.append(site, top, description, access);

    if (artifact.context) {
      const context = document.createElement("span");
      context.className = "room-file-link-context";
      context.textContent = artifact.context;
      copy.append(context);
    }
    const source = document.createElement("span");
    source.className = "room-file-link-source";
    source.textContent = `${artifact.sourceLabel ? `${artifact.sourceLabel} · ` : ""}${formatDate(artifact.createdAt)}`;
    copy.append(source);
    anchor.append(thumb, copy);
    item.append(anchor);
    const resolved = linkPreviewValues.get(artifact.payload.url);
    if (resolved) hydrateLinkPreview(anchor, resolved);
    else observeLinkPreview(anchor, artifact);
    return item;
  }

  function fetchLinkPreview(url: string): Promise<LinkPreviewPayload | null> {
    const cached = linkPreviewCache.get(url);
    if (cached) return cached;
    const request = fetch(`/api/link-preview?url=${encodeURIComponent(url)}`)
      .then(async (response) => {
        const payload: unknown = await response.json().catch(() => ({}));
        if (!response.ok || !isRecord(payload) || payload.ok !== true) return null;
        return projectLinkPreview(payload.preview);
      })
      .catch(() => null);
    linkPreviewCache.set(url, request);
    return request;
  }

  function hydrateLinkPreview(anchor: HTMLAnchorElement, preview: LinkPreviewPayload): void {
    const site = anchor.querySelector<HTMLElement>(".room-file-link-site");
    const title = anchor.querySelector<HTMLElement>(".room-file-link-title");
    const description = anchor.querySelector<HTMLElement>(".room-file-link-description");
    const thumb = anchor.querySelector<HTMLElement>(".room-file-link-thumb");
    const copy = anchor.querySelector<HTMLElement>(".room-file-link-copy");
    if (!site || !title || !description || !thumb || !copy) return;
    site.textContent = preview.siteName || new URL(preview.url).hostname;
    title.textContent = preview.title;
    description.textContent = preview.description || preview.url;
    const priorExcerpt = copy.querySelector<HTMLElement>(".room-file-link-excerpt");
    priorExcerpt?.remove();
    if (preview.excerpt && preview.excerpt !== preview.description) {
      const excerpt = document.createElement("span");
      excerpt.className = "room-file-link-excerpt";
      excerpt.textContent = preview.excerpt;
      const context = copy.querySelector(".room-file-link-context");
      copy.insertBefore(excerpt, context);
    }
    if (preview.image) {
      const image = document.createElement("img");
      image.className = "room-file-link-image";
      image.src = preview.image;
      image.alt = "";
      image.loading = "lazy";
      image.referrerPolicy = "no-referrer";
      image.addEventListener("error", () => {
        thumb.classList.add("room-file-link-thumb--placeholder");
        thumb.textContent = "↗";
      }, { once: true });
      thumb.classList.remove("room-file-link-thumb--placeholder");
      thumb.replaceChildren(image);
    }
  }

  function drainLinkPreviewQueue(): void {
    while (linkPreviewActive < LINK_PREVIEW_CONCURRENCY && linkPreviewQueue.length > 0) {
      const job = linkPreviewQueue.shift();
      if (!job) continue;
      const url = job.link.payload.url;
      if (job.generation !== generation) {
        if (linkPreviewPending.get(url) === job) linkPreviewPending.delete(url);
        continue;
      }
      linkPreviewActive += 1;
      void fetchLinkPreview(url)
        .then((preview) => {
          linkPreviewResolvedUrls.add(url);
          if (!preview || job.generation !== generation) return;
          linkPreviewValues.set(url, preview);
          job.anchors.forEach((anchor) => {
            if (anchor.isConnected) hydrateLinkPreview(anchor, preview);
          });
          if (job.searchRequested && normalizedSearch(searchQuery)) renderSuccess(true);
        })
        .finally(() => {
          if (linkPreviewPending.get(url) === job) linkPreviewPending.delete(url);
          linkPreviewActive -= 1;
          drainLinkPreviewQueue();
        });
    }
  }

  function enqueueLinkPreview(anchor: HTMLAnchorElement | null, link: PreviewableLink, operationGeneration: number, searchRequested = false): void {
    const url = link.payload.url;
    const resolved = linkPreviewValues.get(url);
    if (resolved) {
      if (anchor) hydrateLinkPreview(anchor, resolved);
      return;
    }
    if (linkPreviewResolvedUrls.has(url)) return;
    const pending = linkPreviewPending.get(url);
    if (pending && pending.generation === operationGeneration) {
      if (anchor) pending.anchors.add(anchor);
      pending.searchRequested ||= searchRequested;
      return;
    }
    const job: LinkPreviewJob = {
      anchors: new Set(anchor ? [anchor] : []),
      link,
      generation: operationGeneration,
      searchRequested,
    };
    linkPreviewPending.set(url, job);
    linkPreviewQueue.push(job);
    drainLinkPreviewQueue();
  }

  function requestSearchLinkPreviews(): void {
    if (!normalizedSearch(searchQuery)) return;
    links.forEach((link) => enqueueLinkPreview(null, link, generation, true));
    externalArtifacts.forEach((artifact) => enqueueLinkPreview(null, artifact, generation, true));
  }

  function observeLinkPreview(anchor: HTMLAnchorElement, link: PreviewableLink): void {
    if (typeof window.IntersectionObserver !== "function") {
      enqueueLinkPreview(anchor, link, generation);
      return;
    }
    if (!linkPreviewObserver) {
      linkPreviewObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target as HTMLAnchorElement;
          const pending = linkPreviewTargets.get(target);
          linkPreviewObserver?.unobserve(target);
          if (pending) enqueueLinkPreview(target, pending.link, pending.generation);
        });
      }, { root: list(), rootMargin: "120px" });
    }
    linkPreviewTargets.set(anchor, { link, generation });
    linkPreviewObserver.observe(anchor);
  }

  function resetLinkPreviewObservation(): void {
    linkPreviewObserver?.disconnect();
    linkPreviewQueue = linkPreviewQueue.filter((job) => job.generation === generation);
  }

  function renderLink(link: PublicLink): HTMLElement {
    const item = document.createElement("article");
    item.className = "room-file-item room-file-link-item";
    item.dataset.linkId = link.id;
    const anchor = document.createElement("a");
    anchor.className = "room-file-link-preview";
    anchor.href = link.payload.url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.setAttribute("aria-label", `${link.title} 링크 열기`);
    const thumb = document.createElement("span");
    thumb.className = "room-file-link-thumb room-file-link-thumb--placeholder";
    thumb.textContent = "↗";
    thumb.setAttribute("aria-hidden", "true");
    const copy = document.createElement("span");
    copy.className = "room-file-link-copy";
    const site = document.createElement("span");
    site.className = "room-file-link-site";
    site.textContent = new URL(link.payload.url).hostname;
    const title = document.createElement("strong");
    title.className = "room-file-link-title";
    title.textContent = link.title;
    const description = document.createElement("span");
    description.className = "room-file-link-description";
    description.textContent = link.payload.url;
    copy.append(site, title, description);
    if (link.context) {
      const context = document.createElement("span");
      context.className = "room-file-link-context";
      context.textContent = link.context;
      copy.append(context);
    }
    if (link.sourceLabel) {
      const source = document.createElement("span");
      source.className = "room-file-link-source";
      source.textContent = `${link.sourceLabel} · ${formatDate(link.createdAt)}`;
      copy.append(source);
    }
    anchor.append(thumb, copy);
    item.append(anchor);
    const resolved = linkPreviewValues.get(link.payload.url);
    if (resolved) hydrateLinkPreview(anchor, resolved);
    else observeLinkPreview(anchor, link);
    return item;
  }

  function renderSuccess(preserveStatus = false): void {
    const container = list();
    if (!container) return;
    resetLinkPreviewObservation();
    const total = files.length + artifacts.length + externalArtifacts.length + links.length;
    const visible = visibleItems();
    viewState = total ? "success" : "empty";
    root()?.setAttribute("data-state", viewState);
    syncTabs();
    if (!total) {
      replaceList("이 방에는 아직 자료가 없어요.");
      if (!preserveStatus) setStatus("항목 0개");
      return;
    }
    if (!visible.length) {
      replaceList(searchQuery ? "검색 결과가 없어요." : "이 유형의 항목이 없어요.");
    } else {
      container.replaceChildren(...visible.map((item) => {
        if (item.kind === "file") return renderFile(item.file);
        if (item.kind === "artifact") return renderArtifact(item.artifact);
        if (item.kind === "external_artifact") return renderExternalArtifact(item.artifact);
        return renderLink(item.link);
      }));
    }
    if (!preserveStatus && actionFileId === null) setStatus(`${visible.length}개 / 전체 ${total}개`);
  }

  async function loadFiles(): Promise<void> {
    if (!activeRoomId) return;
    const roomId = activeRoomId;
    const operationGeneration = generation;
    const requestId = ++listRequestId;
    viewState = "loading";
    root()?.setAttribute("data-state", viewState);
    replaceList("자료를 불러오는 중…");
    setStatus("불러오는 중…");
    try {
      const fileRequest = fetch(`/api/files?roomId=${encodeURIComponent(roomId)}`);
      const artifactLinkRequest = activeSessionId
        ? fetch(`/api/artifacts?sessionId=${encodeURIComponent(activeSessionId)}`)
        : null;
      const messageLinkRequest = activeSessionId
        ? fetch(`/api/sessions/${encodeURIComponent(activeSessionId)}/messages/links`)
        : null;
      const response = await fileRequest;
      const payload: unknown = await response.json().catch(() => ({}));
      if (!isCurrentOperation(roomId, operationGeneration) || requestId !== listRequestId) return;
      if (!response.ok || !isRecord(payload) || payload.ok !== true || !Array.isArray(payload.files)) {
        throw new Error("file_list_failed");
      }
      files = payload.files.filter(isPublicFile).sort(newestFirst);
      artifacts = [];
      externalArtifacts = [];
      links = [];
      if (artifactLinkRequest && messageLinkRequest && activeSessionId) {
        const sessionId = activeSessionId;
        const [artifactLinkResponse, messageLinkResponse] = await Promise.all([artifactLinkRequest, messageLinkRequest]);
        const artifactLinkPayload: unknown = await artifactLinkResponse.json().catch(() => ({}));
        const messageLinkPayload: unknown = await messageLinkResponse.json().catch(() => ({}));
        if (!isCurrentOperation(roomId, operationGeneration) || requestId !== listRequestId) return;
        if (!artifactLinkResponse.ok || !isRecord(artifactLinkPayload)
          || artifactLinkPayload.ok !== true || !Array.isArray(artifactLinkPayload.artifacts)) {
          throw new Error("link_list_failed");
        }
        artifacts = artifactLinkPayload.artifacts.flatMap((artifact) => {
          const projected = projectPublicArtifact(artifact, roomId, sessionId);
          return projected ? [projected] : [];
        });
        const materializedFileIds = new Set(artifacts.flatMap((artifact) => artifact.materializedFileIds));
        files = files.filter((file) => !materializedFileIds.has(file.id));
        const rawMessageLinks = messageLinkResponse.ok && isRecord(messageLinkPayload)
          && messageLinkPayload.ok === true && Array.isArray(messageLinkPayload.links)
          ? messageLinkPayload.links
          : [];
        const messageResources = rawMessageLinks.flatMap((link) => {
          const projected = projectMessageLink(link, roomId, sessionId);
          return projected ? [projected] : [];
        });
        externalArtifacts = messageResources.filter((resource): resource is PublicExternalArtifact => resource.resourceType === "external_artifact");
        const messageLinks = messageResources.filter((resource): resource is PublicLink => resource.resourceType === "link");
        const byUrl = new Map<string, PublicLink>();
        messageLinks.forEach((link) => { byUrl.set(link.payload.url, link); });
        links = [...byUrl.values()];
      }
      renderSuccess();
    } catch (_error: unknown) {
      if (!isCurrentOperation(roomId, operationGeneration) || requestId !== listRequestId) return;
      files = [];
      viewState = "error";
      root()?.setAttribute("data-state", viewState);
      artifacts = [];
      externalArtifacts = [];
      links = [];
      replaceList("자료를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.");
      setStatus("불러오기 실패", "error");
    }
  }

  async function open(): Promise<void> {
    const drawer = root();
    if (!drawer || !activeRoomId) return;
    drawer.hidden = false;
    drawer.dataset.open = "true";
    setResizerVisible(true);
    toggle()?.setAttribute("aria-expanded", "true");
    await loadFiles();
  }

  function close(): void {
    const drawer = root();
    if (!drawer) return;
    drawer.hidden = true;
    drawer.dataset.open = "false";
    setResizerVisible(false);
    toggle()?.setAttribute("aria-expanded", "false");
  }

  function setRoom(roomId: string | null): void {
    const nextRoomId = roomId && roomId.trim() ? roomId : null;
    const nextSessionId = nextRoomId && typeof wsClient !== "undefined" && wsClient
      ? wsClient._currentSessionId
      : null;
    if (activeRoomId === nextRoomId && activeSessionId === nextSessionId) return;
    const wasOpen = root()?.dataset.open === "true";
    generation += 1;
    listRequestId += 1;
    linkPreviewObserver?.disconnect();
    linkPreviewQueue = [];
    linkPreviewPending.clear();
    activeRoomId = nextRoomId;
    activeSessionId = nextSessionId;
    files = [];
    artifacts = [];
    externalArtifacts = [];
    links = [];
    activeTab = "all";
    searchQuery = "";
    if (search()) search()!.value = "";
    syncTabs();
    actionFileId = null;
    viewState = "idle";
    replaceList(nextRoomId ? "자료 서랍을 열어 확인하세요." : "대화방에서 자료를 확인할 수 있어요.");
    setStatus("");
    const toggleButton = toggle();
    if (toggleButton) toggleButton.hidden = !nextRoomId;
    if (!nextRoomId) {
      close();
    } else if (wasOpen) {
      void open();
    }
  }

  function reconcileRoomMembership(): void {
    if (!activeRoomId) return;
    const roomList = document.getElementById("roomList");
    if (!roomList) return;
    const stillVisible = Array.from(roomList.querySelectorAll<HTMLElement>(".room-item[data-room-id]"))
      .some((item) => item.dataset.roomId === activeRoomId);
    if (!stillVisible) setRoom(null);
  }

  function bindRoomChanges(): void {
    if (typeof wsClient !== "undefined" && wsClient) {
      const client = wsClient;
      const original = client.setCurrentRoomId.bind(client);
      client.setCurrentRoomId = (roomId: string | null) => {
        original(roomId);
        setRoom(roomId);
      };
      setRoom(client._currentRoomId);
    }
    const roomList = document.getElementById("roomList");
    if (roomList && typeof MutationObserver !== "undefined") {
      new MutationObserver(reconcileRoomMembership).observe(roomList, { childList: true, subtree: true });
    }
  }

  function init(): void {
    if (initialized) return;
    initialized = true;
    initResizer();
    toggle()?.addEventListener("click", () => {
      if (root()?.dataset.open === "true") close();
      else void open();
    });
    document.getElementById("roomFileDrawerClose")?.addEventListener("click", close);
    search()?.addEventListener("input", (event) => {
      searchQuery = (event.currentTarget as HTMLInputElement).value;
      requestSearchLinkPreviews();
      renderSuccess();
    });
    document.getElementById("roomFileDrawerTabs")?.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-file-tab]");
      const tab = button?.dataset.fileTab;
      if (!tab || !["all", "artifact", "document", "media", "file", "link"].includes(tab)) return;
      activeTab = tab as DrawerTab;
      renderSuccess();
    });
    bindRoomChanges();
  }

  const api: DrawerApi = {
    open,
    close,
    setRoom,
    reconcileRoomMembership,
    getState: () => ({ roomId: activeRoomId, view: viewState, fileIds: files.map((file) => file.id) }),
  };
  (window as typeof window & { RoomFileDrawer?: DrawerApi }).RoomFileDrawer = api;
  init();
})();
